import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createContext, useContext, useEffect, useState, useSyncExternalStore } from 'react';
import { FEATURE_GUIDES, FeatureGuideState } from '../featureGuides';
import { FeatureGuideController } from './featureGuideController';
import { FeatureCallout } from './FeatureCallout';
/** The guides' copy and glyphs live in ../featureGuides.ts (plain data the guest page reads too). */
export { FEATURE_GUIDES };
const Context = createContext(undefined);
export const useFeatureGuides = () => useContext(Context);
export function FeatureGuidesProvider({ userId, transport, suppressed = false, children }) {
    // One controller per signed-in user, held with the user it serves, while the children keep one place in the
    // tree whatever the user. Keying the children on the user remounted the whole extension sidebar on sign-out,
    // which threw away the shell's "Session expired" notice in the step that set it (FeatureGuidesProvider.remount.test.ts).
    const [held, setHeld] = useState();
    useEffect(() => {
        if (!userId)
            return;
        const state = new FeatureGuideState(userId, transport);
        const next = new FeatureGuideController(state);
        next.setSuppressed(suppressed);
        setHeld({ userId, controller: next });
        const refresh = () => { void state.refresh(); };
        refresh();
        const interval = window.setInterval(refresh, 30000);
        window.addEventListener('focus', refresh);
        window.addEventListener('online', refresh);
        window.addEventListener('storage', refresh);
        return () => {
            window.clearInterval(interval);
            window.removeEventListener('focus', refresh);
            window.removeEventListener('online', refresh);
            window.removeEventListener('storage', refresh);
            next.dispose();
            setHeld((current) => (current?.controller === next ? undefined : current));
        };
    }, [userId, transport]);
    // React Strict Mode sets this effect up twice; only a live controller made for the current user is handed out.
    const controller = held?.userId === userId ? held.controller : undefined;
    useEffect(() => { controller?.setSuppressed(suppressed); }, [controller, suppressed]);
    return _jsxs(Context.Provider, { value: controller, children: [children, controller && _jsx(GuideCard, { controller: controller })] });
}
function GuideCard({ controller }) {
    useSyncExternalStore(controller.subscribe, controller.getSnapshot, controller.getSnapshot);
    const active = controller.active;
    return active ? _jsx(OpenGuideCard, { controller: controller }, active.id) : null;
}
function OpenGuideCard({ controller }) {
    const active = controller.active;
    const guide = FEATURE_GUIDES[active.id];
    // The one callout with an illustration (FeatureCallout.tsx); the free-tool locked-tool callout renders the same card.
    return _jsx(FeatureCallout, { anchor: active.button, illustration: active.id, icon: guide.icon, title: guide.title, body: guide.description, closeLabel: "Close feature guide", onClose: method => controller.close(method === 'escape' || method === 'close', method === 'hidden' ? 'suppressed' : method), actions: [
            { label: 'Got it', variant: 'secondary', onClick: () => controller.close(true, 'got_it') },
            { label: guide.action, variant: 'primary', onClick: () => { controller.preferences.track('feature_guide_try_clicked', { feature: active.id, placement: 'capture_toolbar' }); controller.close(true, 'try'); active.activate(); } },
        ] });
}
//# sourceMappingURL=FeatureGuides.js.map